import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { useFaqItems } from '../hooks/useFaqItems';
import SiteContentText from './SiteContentText';
import { trackFaqExpand } from '../utils/siteAnalytics';

const ProductScenFaqSection: React.FC = () => {
  const { items, loading } = useFaqItems();

  const scenItems = useMemo(
    () =>
      items
        .filter((item) => item.faq_group === 'scen')
        .sort((a, b) => a.sort_order - b.sort_order || a.question.localeCompare(b.question, 'sv')),
    [items]
  );

  if (loading || scenItems.length === 0) {
    return null;
  }

  return (
    <section className="mb-8" aria-labelledby="product-scen-faq-heading">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 id="product-scen-faq-heading" className="text-xl font-bold text-white flex items-center gap-2">
          <HelpCircle className="text-purple-400 shrink-0" size={22} />
          Vanliga frågor om scen &amp; offert
        </h2>
        <Link
          to="/vanliga-fragor#faq-scen"
          className="text-sm text-purple-300 hover:text-purple-200 underline underline-offset-2"
        >
          Alla scenfrågor
        </Link>
      </div>
      <div className="space-y-3">
        {scenItems.map((faq) => (
          <details
            key={faq.id}
            onToggle={(e) => {
              if ((e.target as HTMLDetailsElement).open) {
                trackFaqExpand(faq.id, faq.question);
              }
            }}
            className="group border border-slate-800 rounded-none overflow-hidden bg-slate-900/20 open:border-purple-500/30"
          >
            <summary className="cursor-pointer list-none p-4 hover:bg-slate-900/30 transition-colors marker:content-none [&::-webkit-details-marker]:hidden">
              <span className="flex items-center justify-between gap-4">
                <span className="font-medium text-white text-left">{faq.question}</span>
                <span className="text-purple-300 shrink-0 transition-transform group-open:rotate-180" aria-hidden>
                  ▼
                </span>
              </span>
            </summary>
            <div className="px-4 pb-4 bg-slate-900/15 border-t border-slate-800">
              <SiteContentText
                as="div"
                className="text-slate-300 text-sm leading-relaxed pt-3 whitespace-pre-line"
                text={faq.answer}
              />
            </div>
          </details>
        ))}
      </div>
    </section>
  );
};

export default ProductScenFaqSection;
